import React, { useEffect, useMemo } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { PageTitle, Loading, Tooltip } from '../root/components/common'
import { NavLink, withRouter } from 'react-router-dom'
import { VictoryLabel, VictoryPie, VictoryTooltip } from 'victory'
import { useSelector, useDispatch } from 'react-redux'
import { useQuery } from 'react-query'
import { getDashboardDevices } from './api/dashboard-api'
import { REPORT_SEVERITY } from '../root/hooks/useReportTypes'
import { enqueueSnackbar } from '../root/actions'
import { getSagasMsgs } from '../root/selectors'
import { OptionSwitcher } from '../root/components/common/option-switcher/option-switcher'
import { setDeviceView } from './actions'
import { REPRESENTATION } from '../root/constants'
import { UserPermissions } from '../../services/permissions'
import { createURL } from '../../utils/hooks/useSyncUrlState'
import { URL_STATUS_SEVERITY_MAP } from '../root/components/common/device-filters/use-device-filters'

const useStyles = makeStyles(theme => ({
    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 24
    },
    wrapper: {
        minHeight: 150
    },
    devicesContainer: {
        display: 'flex',
        '&& > div': {
            flex: 1
        }
    },
    statsContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'space-between',
        '&& > div': {
            fontWeight: 'bold',
            fontSize: 40,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            flex: 1,
            minHeight: '45%',
            minWidth: '45%',
            '& > span': {
                fontSize: 20
            }
        }
    },
    statsContainerTooltip: {
        fontWeight: 'bold',
        fontSize: 40,
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        minWidth: '45%',
        '& > span': {
            fontSize: 20
        }
    },
    stock: {
        color: theme.secondary
    },
    error: {
        color: theme.error
    },
    online: {
        color: theme.active
    },
    warning: {
        color: theme.warning
    },
    link: {
        textDecoration: 'none',
        color: 'inherit',
        '&&:visited': {
            color: 'inherit'
        }
    }
}))

const CHART_CONSTANTS = {
    stock: 'stock',
    error: 'error',
    online: 'online',
    warning: 'warning'
}

function DashboardDevices(props) {
    const classes = useStyles()
    const dispatch = useDispatch()
    const sagasMsgs = useSelector(getSagasMsgs)
    const { devicesView = REPRESENTATION.all } = useSelector(
        state => state.dashboard
    )

    const { isLoading: isLoadingDevices, data = {} } = useQuery(
        'dashboard-devices',
        getDashboardDevices,
        {
            refetchInterval: false,
            onError: e =>
                dispatch(
                    enqueueSnackbar({
                        message: sagasMsgs.getDevicesEx,
                        options: {
                            variant: 'error'
                        }
                    })
                )
        }
    )
    const { theme, locale, isLoading } = useSelector(state => state.main)

    const { assigned = {}, ...rest } = data

    const devicesData = {
        [REPRESENTATION.all]: rest,
        [REPRESENTATION.assigned]: assigned
    }

    const { inactive, critical, active, warning } = devicesData[devicesView]

    const isDevices = Object.values(data).some(Boolean)

    const handleDevicesView = view => {
        dispatch(setDeviceView(view))
    }

    const getChartData = () => {
        return [
            inactive && {
                x: locale.devices.stock,
                y: inactive,
                fill: theme.secondary,
                chartId: REPORT_SEVERITY.inactive
            },
            critical && {
                x: locale.devices.error,
                y: critical,
                fill: theme.error,
                chartId: REPORT_SEVERITY.critical
            },
            active && {
                x: locale.devices.online,
                y: active,
                fill: theme.active,
                chartId: REPORT_SEVERITY.info
            },
            warning && {
                x: locale.devices.warning,
                y: warning,
                fill: theme.warning,
                chartId: REPORT_SEVERITY.warning
            }
        ].filter(Boolean)
    }
    const chartData = useMemo(getChartData, [data, devicesView])

    const onSliceClick = (e, data) => {
        const link = makeLink(
            data.datum.chartId,
            devicesView,
            UserPermissions.isAllOriginalGranted()
        )
        props.history.push(link)
    }

    useEffect(() => {
        if (UserPermissions.isAllOriginalGranted()) {
            dispatch(setDeviceView(REPRESENTATION.all))
        }
    }, UserPermissions.isAllOriginalGranted())

    return (
        <div className={classes.wrapper}>
            <div className={classes.header}>
                <PageTitle
                    className={classes.title}
                    title={locale.devices.devices}
                />
                {UserPermissions.isAllOriginalGranted() && (
                    <OptionSwitcher
                        options={{
                            [REPRESENTATION.all]: locale.devices.allDevices,
                            [REPRESENTATION.assigned]: locale.devices.assigned
                        }}
                        selected={devicesView}
                        onChange={handleDevicesView}
                    />
                )}
            </div>

            {isLoading && <Loading />}

            {!isLoadingDevices && (
                <div className={classes.devicesContainer}>
                    {isDevices && (
                        <div>
                            <VictoryPie
                                style={{
                                    labels: {
                                        fontSize: 10,
                                        fontFamily: theme.fontFamily
                                    },
                                    data: {
                                        fill: ({ datum }) => datum.fill,
                                        cursor: 'pointer'
                                    }
                                }}
                                events={[
                                    {
                                        target: 'data',
                                        eventHandlers: {
                                            onClick: onSliceClick
                                        }
                                    }
                                ]}
                                labelComponent={
                                    <CustomLabel
                                        theme={theme}
                                        locale={locale}
                                    />
                                }
                                padAngle={2}
                                padding={80}
                                innerRadius={70}
                                data={chartData}
                            />
                        </div>
                    )}
                    <div className={classes.statsContainer}>
                        <div className={classes.stock}>
                            <Tooltip
                                hover
                                placement="top"
                                wrapperClass={classes.statsContainerTooltip}
                                text={
                                    locale.dashboard.deviceStatusTooltipText[
                                        REPORT_SEVERITY.inactive
                                    ]
                                }>
                                <span>{locale.devices.stock}</span>
                                <NavLink
                                    className={classes.link}
                                    to={makeLink(
                                        REPORT_SEVERITY.inactive,
                                        devicesView,
                                        UserPermissions.isAllOriginalGranted()
                                    )}>
                                    {inactive}
                                </NavLink>
                            </Tooltip>
                        </div>
                        <div className={classes.error}>
                            <Tooltip
                                hover
                                placement="top"
                                wrapperClass={classes.statsContainerTooltip}
                                text={
                                    locale.dashboard.deviceStatusTooltipText[
                                        REPORT_SEVERITY.critical
                                    ]
                                }>
                                <span>{locale.devices.error}</span>
                                <NavLink
                                    className={classes.link}
                                    to={makeLink(
                                        REPORT_SEVERITY.critical,
                                        devicesView,
                                        UserPermissions.isAllOriginalGranted()
                                    )}>
                                    {critical}
                                </NavLink>
                            </Tooltip>
                        </div>
                        <div className={classes.online}>
                            <Tooltip
                                hover
                                placement="top"
                                wrapperClass={classes.statsContainerTooltip}
                                text={
                                    locale.dashboard.deviceStatusTooltipText[
                                        REPORT_SEVERITY.info
                                    ]
                                }>
                                <span>{locale.devices.online}</span>
                                <NavLink
                                    className={classes.link}
                                    to={makeLink(
                                        REPORT_SEVERITY.info,
                                        devicesView,
                                        UserPermissions.isAllOriginalGranted()
                                    )}>
                                    {active}
                                </NavLink>
                            </Tooltip>
                        </div>
                        <div className={classes.warning}>
                            <Tooltip
                                hover
                                placement="top"
                                wrapperClass={classes.statsContainerTooltip}
                                text={
                                    locale.dashboard.deviceStatusTooltipText[
                                        REPORT_SEVERITY.warning
                                    ]
                                }>
                                <span>{locale.devices.warning}</span>
                                <NavLink
                                    className={classes.link}
                                    to={makeLink(
                                        REPORT_SEVERITY.warning,
                                        devicesView,
                                        UserPermissions.isAllOriginalGranted()
                                    )}>
                                    {warning}
                                </NavLink>
                            </Tooltip>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

function CustomLabelSuper(props) {
    const { theme, locale } = props

    const getTooltipText = data => {
        return (
            'ⓘ ' + locale.dashboard.deviceStatusTooltipText[data.datum.chartId]
        )
    }

    return (
        <g>
            <VictoryLabel {...props} />
            <VictoryTooltip
                {...props}
                flyoutPadding={{ top: 4, bottom: 4, left: 10, right: 10 }}
                flyoutStyle={{
                    fill: theme.background,
                    stroke: theme.primary,
                    strokeWidth: theme.borderWidth
                }}
                style={{ fontSize: '10px' }}
                text={getTooltipText}
                orientation="right"
                pointerLength={0}
                width={100}
                height={100}
                cornerRadius={theme.borderRadius / 2}
            />
        </g>
    )
}

const makeLink = (reportType, assigned, withAssignedToggle) => {
    const filtersState = {
        report_types: URL_STATUS_SEVERITY_MAP[reportType]
    }
    if (withAssignedToggle) {
        filtersState.is_assigned = assigned === REPRESENTATION.assigned
    }
    return `/devices${createURL(filtersState)}`
}

const CustomLabel = Object.assign(CustomLabelSuper, {
    defaultEvents: VictoryTooltip.defaultEvents
})

export default withRouter(DashboardDevices)
